import { createSlice } from '@reduxjs/toolkit';

const SLICE_NAME = 'searchData';

export const SEARCH_SCOPES = {
    VEHICLE: 'vehicle',
    SERVICE: 'service',
};

const SCOPE_FIELDS = {
    [SEARCH_SCOPES.VEHICLE]: {
        data: 'data',
        mode: 'searchMode',
        applied: 'routeFilters',
    },
    [SEARCH_SCOPES.SERVICE]: {
        data: 'searchServiceData',
        mode: 'serviceSearchMode',
        applied: 'serviceRouteFilters',
    },
};

const isSameFilterValue = (current, applied) =>
    String(current ?? '') === String(applied ?? '');

const emptied = (value) => (Object.keys(value).length > 0 ? {} : value);

const releaseRouteKeys = (state, scope, keys) => {
    const applied = state[SCOPE_FIELDS[scope].applied];
    keys.forEach((key) => {
        delete applied[key];
    });
};

const forgetRouteEntry = (state) => {
    if (!state.routeActive) state.routeEntry = null;
};

export const searchDataSlice = createSlice({
    name: SLICE_NAME,
    initialState: {
        data: {},
        searchMode: false,
        searchVehicle: {},
        searchServiceData: {},
        serviceSearchMode: false,
        contextVersion: 0,
        routeFilters: {},
        serviceRouteFilters: {},
        routeEntry: null,
        routeActive: false,
    },
    reducers: {
        saveSearchValues(state, action) {
            const values = action.payload;
            state.data = { ...state.data, ...values };
            state.searchMode = true;
            if (!state.routeActive) {
                releaseRouteKeys(
                    state,
                    SEARCH_SCOPES.VEHICLE,
                    Object.keys(values),
                );
            }
        },
        applyRouteFilters(state, action) {
            const {
                filters,
                entry,
                scope = SEARCH_SCOPES.VEHICLE,
            } = action.payload;
            const { data, mode, applied } = SCOPE_FIELDS[scope];
            const isVehicle = scope === SEARCH_SCOPES.VEHICLE;
            if (Object.keys(filters).length > 0) {
                state[data] = { ...filters };
                if (isVehicle) state.searchVehicle = {};
            } else {
                Object.entries(state[applied]).forEach(([key, value]) => {
                    if (isSameFilterValue(state[data][key], value)) {
                        delete state[data][key];
                        if (isVehicle) delete state.searchVehicle[key];
                    }
                });
            }
            state[applied] = { ...filters };
            state.routeEntry = entry;
            state[mode] = Object.keys(state[data]).length > 0;
        },
        setSearchRouteActive(state, action) {
            state.routeActive = action.payload;
        },
        setSearchVehicle(state, action) {
            const values = action.payload;
            state.searchVehicle = { ...state.searchVehicle, ...values };
        },
        applyVehicleToSearch(state) {
            state.data = { ...state.data, ...state.searchVehicle };
        },
        clearSearchVehicle(state) {
            state.searchVehicle = {};
        },
        clearSearch(state) {
            state.searchMode = false;
            state.data = emptied(state.data);
            state.searchVehicle = emptied(state.searchVehicle);
            state.routeFilters = emptied(state.routeFilters);
            forgetRouteEntry(state);
        },
        resetSearchContext(state) {
            state.searchMode = false;
            state.data = emptied(state.data);
            state.searchVehicle = emptied(state.searchVehicle);
            state.routeFilters = emptied(state.routeFilters);
            state.contextVersion += 1;
            forgetRouteEntry(state);
        },
        clearSearchKeys(state, action) {
            const keys = action.payload || [];

            keys.forEach((key) => {
                delete state.data[key];
                delete state.searchVehicle[key];
            });
            releaseRouteKeys(state, SEARCH_SCOPES.VEHICLE, keys);
            forgetRouteEntry(state);

            state.searchMode = Object.keys(state.data).length > 0;
        },
        setSearchMode(state, action) {
            state.searchMode = action.payload;
        },
        setSearchServiceData(state, action) {
            const values = action.payload || {};
            state.serviceSearchMode = true;
            state.searchServiceData = {
                ...state.searchServiceData,
                ...values,
            };
            if (!state.routeActive) {
                releaseRouteKeys(
                    state,
                    SEARCH_SCOPES.SERVICE,
                    Object.keys(values),
                );
            }
        },
        clearSearchServiceData(state) {
            state.serviceSearchMode = false;
            state.searchServiceData = emptied(state.searchServiceData);
            state.serviceRouteFilters = emptied(state.serviceRouteFilters);
            forgetRouteEntry(state);
        },
        clearSearchServiceKeys(state, action) {
            const keys = action.payload || [];

            keys.forEach((key) => {
                delete state.searchServiceData[key];
            });
            releaseRouteKeys(state, SEARCH_SCOPES.SERVICE, keys);
            forgetRouteEntry(state);

            state.serviceSearchMode =
                Object.keys(state.searchServiceData).length > 0;
        },
    },
});

export const {
    saveSearchValues,
    applyRouteFilters,
    setSearchRouteActive,
    setSearchVehicle,
    applyVehicleToSearch,
    clearSearchVehicle,
    clearSearch,
    resetSearchContext,
    clearSearchKeys,
    setSearchMode,
    setSearchServiceData,
    clearSearchServiceData,
    clearSearchServiceKeys,
} = searchDataSlice.actions;

export const selectSearchMode = (state) => state.search.searchData.searchMode;

export const selectSearchData = (state) => state.search.searchData.data;

export const selectSearchServiceData = (state) =>
    state.search.searchData.searchServiceData;

export const selectServiceSearchMode = (state) =>
    state.search.searchData.serviceSearchMode;

export const selectSearchVehicle = (state) =>
    state.search.searchData.searchVehicle;

export const selectSearchContextVersion = (state) =>
    state.search.searchData.contextVersion;

export const selectSearchRouteEntry = (state) =>
    state.search.searchData.routeEntry;

export const selectSearchRouteFilters = (scope) => (state) =>
    state.search.searchData[SCOPE_FIELDS[scope].applied];

export const searchData = searchDataSlice.reducer;
